/**
 * Farmer Network — Round 3 Part A §3.
 * Only TWO elements remain: "Register Your Farm" tab + official Google Form link.
 * No sign-in / "Already registered?" path. No dual confirmation. Verification
 * happens ONLY in the backend Admin Console (guardrail).
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ExternalLink, Sprout, IndianRupee, TrendingUp } from "lucide-react";

const HERO = "/photos-r4/farmer-network-turban.webp";
const GOOGLE_FORM_URL = "https://forms.gle/jFtZaX3Y97wQkhzz6";

export default function FarmerNetwork() {
  const { t } = useTranslation();

  return (
    <div data-testid="page-farmer-network">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Indian farmer in field" className="w-full h-full object-cover" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("farmer.hero_eyebrow")}</div>
          <h1 className="tr-rise mt-4 font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight max-w-4xl">
            {t("farmer.hero_title_a")}<br />
            <span className="text-[var(--tr-citrus-yellow)]">{t("farmer.hero_title_b")}</span> {t("farmer.hero_title_c")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("farmer.hero_body")}</p>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="farmer-hero-cta-block">
            <Link to="/farmer-interest" data-testid="farmer-register-your-farm" className="tr-pill-btn tr-btn-zest">
              {t("home.register_your_farm")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="farmer-google-form-link"
              className="tr-pill-btn tr-btn-outline !bg-white/10 !text-white !border-white/30"
            >
              {t("farmer.gform_cta")} <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: IndianRupee, k: "price" },
          { icon: Sprout, k: "agro" },
          { icon: TrendingUp, k: "payout" },
        ].map((b) => (
          <div key={b.k} className="tr-card p-8">
            <b.icon className="w-6 h-6 text-[var(--tr-zest)]" />
            <div className="mt-4 font-display font-extrabold text-xl text-[var(--tr-deep-green)]">{t(`farmer.b_${b.k}_t`)}</div>
            <p className="mt-2 text-[var(--tr-muted)] leading-relaxed">{t(`farmer.b_${b.k}_d`)}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div
          data-testid="farmer-register-card"
          className="relative overflow-hidden rounded-3xl bg-[var(--tr-deep-green)] tr-grain text-white p-8 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
        >
          <div className="lg:col-span-8">
            <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">
              {t("farmer.gform_eyebrow")}
            </div>
            <h2 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[0.95] tracking-tight">
              {t("farmer.gform_title")}
            </h2>
            <p className="mt-5 text-white/85 leading-relaxed max-w-2xl">{t("farmer.gform_body")}</p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link data-testid="farmer-register-cta-bottom" to="/farmer-interest" className="tr-pill-btn tr-btn-zest">
              {t("home.register_your_farm")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="farmer-google-form-link-bottom"
              className="tr-pill-btn tr-btn-outline !bg-white/10 !text-white !border-white/30"
            >
              {t("farmer.gform_cta")} <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
